/**
 * RBAC capability constants — mirrors backend app/auth/roles.py. Use these
 * with `useCan(...)` instead of the legacy `isOwner` boolean. See
 * features/roles_and_access.md.
 */
export const CAP = {
  chartsView: "charts.view",
  analysisRun: "analysis.run",
  tradingRead: "trading.read",
  tradingWrite: "trading.write",
  journalRead: "journal.read",
  journalWrite: "journal.write",
  notesRead: "notes.read",
  notesWrite: "notes.write",
  dashboardView: "dashboard.view",
  usersManage: "users.manage",
  rolesManage: "roles.manage",
  terminalUse: "terminal.use",
  reportsView: "reports.view",
  tradersManage: "traders.manage",
  adminsManage: "admins.manage",
  chatSupport: "chat.support",
  chatSupportAll: "chat.support.all",
  usageView: "usage.view",
} as const;

export type Capability = (typeof CAP)[keyof typeof CAP];

/** Display metadata for the fixed roles (label + pill accent). */
export const ROLE_META: Record<
  string,
  {
    label: string;
    tone: "owner" | "admin" | "maintenance" | "supporter" | "trader" | "analyst" | "viewer";
  }
> = {
  owner: { label: "Owner", tone: "owner" },
  admin: { label: "Admin", tone: "admin" },
  maintenance: { label: "Maintenance", tone: "maintenance" },
  supporter: { label: "Supporter", tone: "supporter" },
  trader: { label: "Trader", tone: "trader" },
  analyst: { label: "Analyst", tone: "analyst" },
  viewer: { label: "Viewer", tone: "viewer" },
};

/** Roles ordered most → least privileged (for the picker + matrix). */
export const ROLE_ORDER = [
  "owner",
  "admin",
  "maintenance",
  "supporter",
  "trader",
  "analyst",
  "viewer",
] as const;

/** Roles that can be granted from the dashboard. Not owner (it comes from
 *  server config only) and not viewer (the baseline: holding no role). */
export const ASSIGNABLE_ROLES = ROLE_ORDER.filter((r) => r !== "owner" && r !== "viewer");

export function roleLabel(role: string | undefined): string {
  return (role && ROLE_META[role]?.label) || "Viewer";
}

/** A user's role set for display: "Supporter · Trader", or "Viewer" for none. */
export function rolesLabel(roles: readonly string[] | undefined): string {
  return roles && roles.length ? roles.map(roleLabel).join(" · ") : "Viewer";
}
